import type { SeriesSelection, SeriesSelectionByTargetId } from '../types/SeriesSelection.types';

/**
 * Pure transitions for the per-target series selection. Kept outside React so the invariant
 * "a highlighted series is always a selected series" lives in one testable place.
 */
export function changeSelectedSeries(selection: SeriesSelection, selectedSeriesIds: string[]): SeriesSelection {
  const selected = new Set(selectedSeriesIds);

  return {
    selectedSeriesIds,
    highlightedSeriesIds: selection.highlightedSeriesIds.filter((seriesId) => selected.has(seriesId)),
  };
}

export function deselectSeries(selection: SeriesSelection, seriesId: string): SeriesSelection {
  return changeSelectedSeries(
    selection,
    selection.selectedSeriesIds.filter((selectedId) => selectedId !== seriesId)
  );
}

export function toggleHighlightedSeries(selection: SeriesSelection, seriesId: string): SeriesSelection {
  if (!selection.selectedSeriesIds.includes(seriesId)) {
    return selection;
  }

  const isHighlighted = selection.highlightedSeriesIds.includes(seriesId);

  return {
    ...selection,
    highlightedSeriesIds: isHighlighted
      ? selection.highlightedSeriesIds.filter((highlightedId) => highlightedId !== seriesId)
      : [...selection.highlightedSeriesIds, seriesId],
  };
}

type RememberSelectionParams = {
  selections: SeriesSelectionByTargetId;
  targetId: number;
  selection: SeriesSelection;
  updatedAt: number;
  maxRememberedTargets: number;
};

/**
 * Keeps the storage bounded by dropping the least recently updated targets. Recency is stored
 * explicitly: target ids are integer-like keys, which JavaScript iterates in ascending numeric
 * order, so key order says nothing about age.
 */
export function rememberSelection({
  selections,
  targetId,
  selection,
  updatedAt,
  maxRememberedTargets,
}: RememberSelectionParams): SeriesSelectionByTargetId {
  const next: SeriesSelectionByTargetId = { ...selections, [targetId]: { ...selection, updatedAt } };
  const entries = Object.entries(next);

  if (entries.length <= maxRememberedTargets) {
    return next;
  }

  const mostRecentFirst = entries.sort(([, first], [, second]) => (second?.updatedAt ?? 0) - (first?.updatedAt ?? 0));

  return Object.fromEntries(mostRecentFirst.slice(0, maxRememberedTargets));
}
